import { EAxiosResponseCode, getHttpErrorMessage, Http } from '@core/api';
import { getHostbaseUrl, isNull } from '@core/tools';
// eslint-disable-next-line @stylistic/js/max-len
import { EPaymentChannel, IBindZsServerAccountParam, IConfirmReconciliationParam, IFinanceMonthEndParam, IFinanceServerAccount, IFinanceServerAccountParam, IFinanceSubunit, IFinanceSubunitParam, IFinanceTransaction, IFinanceTransactionParam, IFinanceWallet, IInternalTransferParam, IReconiliationList, IReconiliationListParam, IServerProvider, ISubunitPayerItem, ISubunitWithDrawItem, IUnitOption, IZsWithdrawAccount, IZsWithdrawParams } from '../../types';
import { EFinanceDepositStatus, ESubunitStatus, EZSAbnormalRecordsMethod, ICreateUnitParam, IFinanceAgentUnit, IZSAbnormalRecordsItem, IZSAbnormalRecordsParams, IZSFundItem, IZSFundParams, IZSReceiptForm, IZSReceiptItem, IZSReceiptParams, IZsUnitModifyNameRequest } from '../../types/finance';

const hostUrl = getHostbaseUrl();

/**
 * 财务管理 - 服务商招行账户列表查询
 */
export async function queryFinanceServerAccounts(params: IFinanceServerAccountParam) {
  let total = 0;
  let data = [] as IFinanceServerAccount[];

  try {
    const { code, context, message } = await Http.post<{ data: IFinanceServerAccount[]; total: number }>(
      `${hostUrl}/pay/zs/server/page`,
      { ...params },
    );

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as { data: IFinanceServerAccount[]; total: number };

      data = res.data || [];
      total = res.total || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 财务管理 - 服务商招行账户解绑
 */
export async function unBindZSAccount(zsServerId: number) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/zs/server/delete/${zsServerId}`,
      {},
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 财务管理 - 月末余额分页查询
 */
export async function queryFinanceMonthEndData(params: IFinanceMonthEndParam) {
  let total = 0;
  let data = [] as any[];

  try {
    const { code, context, message } = await Http.post<{ data: any[]; total: number }>(
      `${hostUrl}/pay/zs/server/month-page`,
      { ...params },
    );

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as { data: any[]; total: number };

      data = res.data || [];
      total = res.total || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 财务管理 - 下载月统计
 */
export async function downMonthEndInfo(monthId: number) {
  let buffer = null as Blob | null;

  try {
    const { code, context, message } = await Http.get<Blob>(
      `${hostUrl}/pay/zs/server/month-download`,
      { monthId },
      { responseType: 'blob' },
    );

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return buffer;
}

/**
 * 财务管理 - 子单元列表查询
 */
export async function queryFinanceSubunits(params: IFinanceSubunitParam) {
  let total = 0;
  let data = [] as IFinanceSubunit[];

  try {
    const { code, context, message } = await Http.post<{ data: IFinanceSubunit[]; total: number }>(
      `${hostUrl}/pay/zs/unit/page`,
      { ...params },
    );

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as { data: IFinanceSubunit[]; total: number };

      data = res.data || [];
      total = res.total || 0;

      for (const v of data) {
        v.showDeleteUnit = isNull(v.depositFlag) || v.enabled === ESubunitStatus.Closed;
        v.showOnDepositFlag = v.enabled === ESubunitStatus.Opened && v.depositFlag === EFinanceDepositStatus.Disabled;
        v.showOffDepositFlag = v.enabled === ESubunitStatus.Opened && v.depositFlag === EFinanceDepositStatus.Enabled;
      }
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 财务管理 - 交易管理列表查询
 */
export async function queryFinanceTransactions(params: IFinanceTransactionParam) {
  let total = 0;
  let data = [] as IFinanceTransaction[];

  try {
    const { code, context, message } = await Http.post<{ data: IFinanceTransaction[]; total: number }>(
      `${hostUrl}/pay/fund/page`,
      { ...params },
    );

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as { data: IFinanceTransaction[]; total: number };

      data = res.data || [];
      total = res.total || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 财务管理 - 子单元管理 - 付款方账号分页查询
 */
export async function querySubunitPayerList(params: any, unitId: number) {
  let total = 0;
  let data = [] as ISubunitPayerItem[];

  try {
    const { code, context, message } = await Http.post<{ data: ISubunitPayerItem[]; total: number }>(
      `${hostUrl}/pay/zs/unit/payer/page`,
      {
        ...params,
        unitId,
        total: undefined,
      },
    );

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as { data: ISubunitPayerItem[]; total: number };

      data = res.data || [];
      total = res.total || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 财务管理 - 子单元管理 - 提现账号分页查询
 */
export async function querySubunitWithdrawList(params: any, unitId: number) {
  let total = 0;
  let data = [] as ISubunitWithDrawItem[];

  try {
    const { code, context, message } = await Http.post<{ data: ISubunitWithDrawItem[]; total: number }>(
      `${hostUrl}/pay/zs/unit/withdraw/page`,
      {
        ...params,
        unitId,
        total: undefined,
      },
    );

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as { data: ISubunitWithDrawItem[]; total: number };

      data = res.data || [];
      total = res.total || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 财务管理 - 禁止-允许入账
 */
export async function updateDepositStatus(unitId: number, status: EFinanceDepositStatus) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/zs/unit/change-deposit`,
      {
        depositFlag: status,
        unitId,
      },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 财务管理 - 关闭子单元
 */
export async function disableSubunit(unitId: number) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/zs/unit/close/${unitId}`,
      {},
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 财务管理 - 删除子单元
 */
export async function removeSubunit(unitId: number) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/zs/unit/delete/${unitId}`,
      {},
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 财务管理 - 银企对账列表查询
 */
export async function queryReconciliationList(params: IReconiliationListParam) {
  let total = 0;
  let data = [] as IReconiliationList[];

  try {
    const { code, context, message } = await Http.post<{ data: IReconiliationList[]; total: number }>(
      `${hostUrl}/pay/zs/check/account/list`,
      { ...params },
    );

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as { data: IReconiliationList[]; total: number };

      data = res.data || [];
      total = res.total || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 财务管理 - 确认银企对账
 */
export async function confirmReconciliation(param: IConfirmReconciliationParam) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/zs/check/account`,
      { ...param },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 财务管理 - 批量确认银企对账
 */
export async function batchConfirmReconciliation(params: IConfirmReconciliationParam[]) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/zs/check/account/batch`,
      { ...params },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 财务管理 - 子单元管理 - 重新关联付款方账号
 */
export async function reConnectSubunitPayerAccount(payId: number) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/zs/unit/payer/relation/${payId}`,
      {},
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 财务管理 - 子单元管理 - 删除付款方账号
 */
export async function deleteSubunitPayerAccount(payerId: number) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/zs/unit/payer/delete/${payerId}`,
      {},
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 财务管理 - 子单元管理 - 删除提现账号
 */
export async function deleteSubunitWithdrawAccount(withdrawId: number) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/zs/unit/withdraw/delete/${withdrawId}`,
      {},
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 财务管理 - 子单元管理 - 新增/修改提现账号
 */
export async function saveSubunitWithdrawAccount(params: ISubunitWithDrawItem, editMode = false) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/zs/unit/withdraw/${editMode ? 'update' : 'create'}`,
      {
        ...params,
        withdrawId: editMode ? params.withdrawId : undefined,
      },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 财务管理 - 关联付款方账号新增/修改
 */
export async function saveReConnectPayerAccount(v: { unitId: number; payerId: number; editMode: boolean; payAccount: string }) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/zs/unit/payer/${v.editMode ? 'update' : 'create'}`,
      {
        payAccount: v.payAccount,
        unitId: !v.editMode ? v.unitId : undefined,
        payerId: v.editMode ? v.payerId : undefined,
      },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 财务管理 - 招商银行的服务商列表查询
 */
export async function queryServerProviders() {
  let data = [] as Array<IServerProvider>;

  try {
    const { code, context, message } = await Http.get<Array<IServerProvider>>(
      `${hostUrl}/pay/zs/server/server-list`,
      {},
    );

    if (code === EAxiosResponseCode.Succeed) {
      data = context || [];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/** 获取子单元下拉选项 */
export async function queryUnitOptions(zsServerId: number) {
  let data = [] as Array<IUnitOption>;

  try {
    const { code, context, message } = await Http.get<Array<IUnitOption>>(
      `${hostUrl}/pay/zs/unit/list`,
      { zsServerId },
    );

    if (code === EAxiosResponseCode.Succeed) {
      data = context || [];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/** 获取子单元下拉选项 */
export async function queryZsWithdrawAccounts(accountId: number) {
  let data = [] as Array<IZsWithdrawAccount>;

  try {
    const { code, context, message } = await Http.post<Array<IZsWithdrawAccount>>(
      `${hostUrl}/pay/zs/unit/withdraw/list`,
      { unitId: accountId },
    );

    if (code === EAxiosResponseCode.Succeed) {
      data = context || [];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 招行提现
 */
export async function doZsWithdraw(params: IZsWithdrawParams) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/zs/unit/withdraw/cash`,
      { ...params },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 招行提现 - 查询提现卡信息
 */
export async function checkWithdrawAcct(accountId: number, payType: EPaymentChannel) {
  let hasCrad = true;

  try {
    const { code } = await Http.post<void>(
      `${hostUrl}/pay/account/checkWithdrawAcct`,
      {
        accountId,
        payType,
      },
    );

    if (Number(code) === 10011) {
      hasCrad = false;
    }
  } catch (error) {
    console.log(getHttpErrorMessage(error));
  }

  return hasCrad;
}

/**
 * 财务管理 - 招商银行的分行号列表查询
 */
export async function queryBranchNumList() {
  let data = [] as Array<{ description: string; code: string }>;

  try {
    const { code, context, message } = await Http.get<Array<{ description: string; code: string }>>(
      `${hostUrl}/pay/zs/server/branch-num-list`,
      {},
    );

    if (code === EAxiosResponseCode.Succeed) {
      data = context || [];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 财务管理 - 绑定服务商招行信息
 */
export async function bindZsServerAccount(param: IBindZsServerAccountParam) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/zs/server/create`,
      { ...param },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 财务管理 - 招商银行 - 内部转账
 */
export async function internalTransfer(param: IInternalTransferParam) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/zs/server/inner-transfer`,
      { ...param },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 财务管理 - 获取钱包账户信息
 */
export async function queryFinanceWallets(justUsable: boolean = false) {
  let data = [] as Array<IFinanceWallet>;

  try {
    const { code, context, message } = await Http.post<Array<IFinanceWallet>>(
      `${hostUrl}/pay/account/purses`,
      { justUsable },
    );

    if (code === EAxiosResponseCode.Succeed) {
      data = context || [];

      data.forEach((item) => {
        item.withdrawableAmount = item.withdrawableAmount || 0;
      });
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 *  子单元导出
 */

export async function exportUnitPageList(params) {
  let buffer = null as Blob | null;

  try {
    const { code, context, message } = await Http.get<Blob>(
      `${hostUrl}/pay/zs/unit/export`,
      { ...params },
      { responseType: 'blob' },
    );

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return buffer;
}

/**
 *  子单元明细导出
 */

export async function exportUnitDetailList(params) {
  let buffer = null as Blob | null;

  try {
    const { code, context, message } = await Http.post<Blob>(
      `${hostUrl}/pay/zs/unit/units-under-server-export`,
      { ...params },
      { responseType: 'blob' },
    );

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return buffer;
}

/**
 * 导出交易管理
 */
export async function exportTransOrders(params: IFinanceTransactionParam) {
  let buffer = null as Blob | null;

  try {
    const { code, context, message } = await Http.get<Blob>(
      `${hostUrl}/pay/fund/export`,
      params,
      { responseType: 'blob' },
    );

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return buffer;
}

/**
 * 导出收款单
 */
export async function exportReceipt(params: IFinanceTransactionParam) {
  try {
    const { code, message } = await Http.post<string>(
      `${hostUrl}/pay/fund/batch-download`,
      params,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 财务管理 - 校验特代是否授权，下级代理商是否开通子单元以及获取当前服务商下已授权的特级代理商
 */
export async function queryAgentUnits(zsServerId: number) {
  let data = [] as IFinanceAgentUnit[];

  try {
    const { code, context, message } = await Http.get<IFinanceAgentUnit[]>(
      `${hostUrl}/pay/zs/unit/check-Agent-units`,
      { zsServerId },
    );

    if (code === EAxiosResponseCode.Succeed) {
      data = context || [];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

export async function queryUnitAgents(topAgentId: number, zsServerId: number) {
  let data = [] as IFinanceAgentUnit[];

  try {
    const { code, context, message } = await Http.get<IFinanceAgentUnit[]>(
      `${hostUrl}/pay/zs/unit/agent-list`,
      {
        topAgentId,
        zsServerId,
      },
    );

    if (code === EAxiosResponseCode.Succeed) {
      data = context || [];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 财务管理 - 创建子单元
 */
export async function postCreateUnit(param: ICreateUnitParam) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/zs/unit/create`,
      { ...param },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}
export * from './finance-linalian';
export * from './finance-lianlian-utils';

/** 
 * 财务管理 - 招商交易流水分页查询 
 * */
export async function queryZSFundPage(params: IZSFundParams) {
  let total = 0;
  let data = [] as IZSFundItem[];

  try {
    const { code, context, message } = await Http.post<{ data: IZSFundItem[]; total: number }>(
      `${hostUrl}/pay/zs/fund/page`,
      { ...params },
    );

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as { data: IZSFundItem[]; total: number };

      data = res.data || [];
      total = res.total || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 财务管理 - 招商交易流水交易记录导出
 */

export async function exportZSFundPage(params: IZSFundParams) {
  let buffer = null as Blob | null;
  let fileName = ''; // 默认文件名

  try {
    const { code, context, message, headers } = await Http.get<Blob>(
      `${hostUrl}/pay/zs/fund/export`,
      { ...params },
      { responseType: 'blob' },
    );

    if (headers['content-disposition']) {
      const match = headers['content-disposition'].match(/filename=(.+);?/);

      if (match && match[1]) {
        fileName = decodeURIComponent(match[1].trim().replace(/"/g, ''));
      }
    }
    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    buffer,
    fileName,
  };
}

/** 
 * 财务管理 - 招商交易 - 回单记录
 * */
export async function queryZSReceiptPage(params: IZSReceiptParams) {
  let total = 0;
  let data = [] as IZSReceiptItem[];

  try {
    const { code, context, message } = await Http.post<{ data: IZSReceiptItem[]; total: number }>(`${hostUrl}/pay/zs/receipt/page`, params);

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as { data: IZSReceiptItem[]; total: number };

      data = res.data || [];
      total = res.total || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/** 
 * 财务管理 - 招商交易 - 回单记录 - 删除
 * */
export async function deleteZSReceipt(receiptId: number) {
  try {
    const { code, message } = await Http.post<void>(`${hostUrl}/pay/zs/receipt/delete/${receiptId}`);

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/** 
 * 财务管理 - 招商交易 - 回单记录 - 上传
 * */
export async function uploadZSReceipt(params: IZSReceiptForm) {
  try {
    const { code, message } = await Http.post<void>(`${hostUrl}/pay/zs/receipt/save`, params);

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/** 
 * 财务管理 - 招商企业子单元 - 异常充值记录
 * */
export async function queryZSAbnormalRecordsPage(params: IZSAbnormalRecordsParams) {
  let total = 0;
  let data = [] as IZSAbnormalRecordsItem[];

  try {
    const { code, context, message } = await Http.post<{ data: IZSAbnormalRecordsItem[]; total: number }>(
      `${hostUrl}/pay/zs/fund/error-recharge-page`,
      { ...params },
    );

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as { data: IZSAbnormalRecordsItem[]; total: number };

      data = res.data || [];
      total = res.total || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 财务管理 - 招商企业子单元 - 异常充值记录 - 导出
 */

export async function exportZSAbnormalRecords(params: IZSAbnormalRecordsParams) {
  let buffer = null as Blob | null;
  let fileName = ''; // 默认文件名

  try {
    const { code, context, message, headers } = await Http.post<Blob>(
      `${hostUrl}/pay/zs/fund/error-recharge-export`,
      { ...params },
      { responseType: 'blob' },
    );

    if (headers['content-disposition']) {
      const match = headers['content-disposition'].match(/filename=(.+);?/);

      if (match && match[1]) {
        fileName = decodeURIComponent(match[1].trim().replace(/"/g, ''));
      }
    }
    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    buffer,
    fileName,
  };
}

/**
 * 财务管理 - 企业子单元 - 招商交易流水-待处理的异常充值记录的数量
 */
export async function queryFinanceWarningCount() {
  let count = 0;

  try {
    const { code, context, message } = await Http.get<number>(`${hostUrl}/pay/zs/fund/error-recharge-count`);

    if (code === EAxiosResponseCode.Succeed) {
      count = context || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return count;
}

/** 
 * 财务管理 - 招商企业子单元 - 招商交易流水-处理企业异常充值
 * */
export async function handleZSAbnormalRecords(fundId: number, handleType: EZSAbnormalRecordsMethod, handleRemark: string) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/zs/fund/handle-error-recharge`,
      {
        fundId,
        handleType,
        handleRemark,
      },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}
/**
 * 修改企业记账子单元名称
 */
export async function modifyZsUnitName(params: IZsUnitModifyNameRequest) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/zs/unit/company/modify-name`,
      params,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}
